import { useState } from 'react';
import {
  ChartFrame,
  EmptyChart,
  INK,
  Legend,
  MotionStyles,
  moneyText,
  seriesColour,
  useMeasuredWidth,
  usePrefersReducedMotion,
  type ChartTable,
  type TooltipState,
} from './Chart';
import { linearScale, niceDomain } from './layout';

/**
 * Horizontal bars, one per row, with an optional darker part of each.
 *
 * ── WHAT IT IS FOR ──
 * "Cash on hand by book" and "Owed to you by book": one bar per book in the
 * book's colour, the value at its tip. On the receivables chart the overdue
 * part is the same colour at full strength and the rest is a tint, so the
 * bar reads as one amount with a darker piece of it late - which is what
 * overdue IS, a part of what is owed, not a second figure beside it.
 *
 * A negative value (an overdrawn account) draws leftwards from zero, so a
 * loss of cash is a bar pointing the other way rather than a bar that lies
 * about its sign.
 *
 * ── THE NAME IS NEVER CUT ──
 * Beside the bar when the longest name fits in a third of the width; above
 * it when it does not. A phone gave the label column 114px, which is
 * "Acme Consultin…" - and the name is the one thing that says whose bar it
 * is. Two lines per book costs height, which a phone has, over width, which
 * it does not.
 */

export interface HBarRow {
  id: string;
  name: string;
  /** A palette token, or null for the theme's primary. */
  colour: string | null;
  value: number;
  /** The darker part, drawn from zero inside the bar. Overdue, of receivables. */
  part?: number;
}

export interface HBarsProps {
  rows: HBarRow[];
  currency: string;
  title: string;
  summary: string;
  /** What `value` is, for the table column: "Cash". */
  valueLabel: string;
  /** What `part` is, for the legend, the tooltip and the table: "Overdue". */
  partLabel?: string;
  empty: { title: string; description?: string };
}

const BAR = 16;
const LABEL_WIDTH = 0.32;
const VALUE_GUTTER = 8;
/** Roughly what one character of a 12px name takes. */
const CHAR = 6.5;

export function HBars({ rows, currency, title, summary, valueLabel, partLabel, empty }: HBarsProps) {
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState<number | null>(null);

  if (rows.length === 0 || rows.every((row) => row.value === 0 && !row.part)) {
    return <EmptyChart title={empty.title} description={empty.description} />;
  }

  const longestName = Math.max(...rows.map((row) => row.name.length));
  const sideLabelWidth = Math.round(width * LABEL_WIDTH);
  // Name above the bar when it would not fit beside it - see the header.
  const stacked = longestName * CHAR > sideLabelWidth;
  const ROW = stacked ? 46 : 30;
  const height = rows.length * ROW + 4;
  const labelWidth = stacked ? 0 : sideLabelWidth;
  const plotLeft = stacked ? 2 : labelWidth + 8;
  // The bar's vertical position within its row: centred beside the name,
  // or under it.
  const barTop = (i: number) => (stacked ? i * ROW + 24 : i * ROW + (ROW - BAR) / 2 + 2);
  // Room on the right for the longest value label, measured in characters.
  const longest = Math.max(...rows.map((row) => moneyText(row.value, currency).length));
  const plotRight = Math.max(plotLeft + 40, width - longest * 7 - VALUE_GUTTER);
  // Above the bar a name has the whole width; one longer than that is the
  // only one ever shortened, and the table still has it in full.
  const fit = Math.max(8, Math.floor((width - 4) / CHAR));
  const clip = (name: string) => (name.length > fit ? `${name.slice(0, fit - 1)}…` : name);
  const lo = Math.min(0, ...rows.map((row) => row.value));
  const hi = Math.max(0, ...rows.map((row) => row.value));
  const x = linearScale(niceDomain(lo, hi, 4), [plotLeft, plotRight]);
  const zero = x(0);
  const hasParts = rows.some((row) => row.part !== undefined);

  const tooltipFor = (i: number): TooltipState => {
    const row = rows[i];
    if (!row) return { x: zero, y: 0, title: '', rows: [] };
    return {
      x: x(row.value),
      y: barTop(i),
      title: row.name,
      rows: [
        { key: 'value', label: valueLabel, value: moneyText(row.value, currency) },
        ...(row.part !== undefined && partLabel ? [{ key: 'part', label: partLabel, value: moneyText(row.part, currency) }] : []),
      ],
    };
  };

  const table: ChartTable = {
    caption: `${title}, in ${currency}`,
    columns: ['Book', valueLabel, ...(hasParts && partLabel ? [partLabel] : [])],
    rows: rows.map((row) => ({
      key: row.id,
      cells: [row.name, moneyText(row.value, currency), ...(hasParts && partLabel ? [moneyText(row.part ?? 0, currency)] : [])],
    })),
  };

  return (
    <div className="space-y-2">
      <ChartFrame title={title} table={table} tooltip={active === null ? null : tooltipFor(active)} frameRef={frameRef}>
        <svg role="img" aria-label={summary} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="block select-none">
          <defs>
            <MotionStyles enabled={!reduced} />
          </defs>
          {/* The zero line runs the bars' height only; above a stacked
              row's name it would read as a rule through the text. */}
          {stacked ? (
            rows.map((row, i) => (
              <line key={row.id} aria-hidden x1={zero} x2={zero} y1={barTop(i) - 3} y2={barTop(i) + BAR + 3} stroke={INK.zero} strokeWidth={1} shapeRendering="crispEdges" />
            ))
          ) : (
            <line aria-hidden x1={zero} x2={zero} y1={0} y2={height} stroke={INK.zero} strokeWidth={1} shapeRendering="crispEdges" />
          )}
          {rows.map((row, i) => {
            const top = barTop(i);
            const ink = seriesColour(row.colour);
            const start = Math.min(zero, x(row.value));
            const end = Math.max(zero, x(row.value));
            const partEnd = row.part !== undefined ? x(Math.max(0, Math.min(row.part, row.value))) : null;
            // The value sits past the bar's far end - or, for a bar pointing
            // left, just right of zero, in the air, rather than into the
            // row's label.
            const valueX = row.value < 0 ? zero + VALUE_GUTTER : end + VALUE_GUTTER;
            return (
              <g
                key={row.id}
                onPointerEnter={(event) => {
                  if (event.pointerType !== 'touch') setActive(i);
                }}
                onPointerLeave={(event) => {
                  if (event.pointerType !== 'touch') setActive(null);
                }}
                onPointerDown={(event) => {
                  if (event.pointerType === 'touch') setActive((current) => (current === i ? null : i));
                }}
              >
                <title>{`${row.name}: ${moneyText(row.value, currency)}${row.part !== undefined && partLabel ? ` · ${partLabel.toLowerCase()} ${moneyText(row.part, currency)}` : ''}`}</title>
                {/* A hit area the height of the row, so the pointer never has to land on a 16px bar. */}
                <rect x={0} y={i * ROW} width={width} height={ROW} fill={active === i ? 'hsl(var(--foreground) / 0.04)' : 'transparent'} />
                {stacked ? (
                  <text x={plotLeft} y={i * ROW + 12} dy="0.35em" textAnchor="start" fontSize={12} fill={INK.text}>
                    {clip(row.name)}
                  </text>
                ) : (
                  <text x={labelWidth} y={top + BAR / 2} dy="0.35em" textAnchor="end" fontSize={12} fill={INK.text}>
                    {row.name}
                  </text>
                )}
                <rect
                  x={start}
                  y={top}
                  width={Math.max(row.value === 0 ? 0 : 2, end - start)}
                  height={BAR}
                  rx={2}
                  fill={ink}
                  opacity={hasParts ? 0.4 : 1}
                  style={reduced ? undefined : { transformOrigin: `${zero}px ${top}px`, animation: 'chart-grow-x 450ms ease-out both' }}
                />
                {partEnd !== null && partEnd > zero ? (
                  <rect x={zero} y={top} width={partEnd - zero} height={BAR} rx={2} fill={ink} />
                ) : null}
                <text
                  x={valueX}
                  y={top + BAR / 2}
                  dy="0.35em"
                  textAnchor="start"
                  fontSize={12}
                  fill={INK.text}
                  className="tabular-nums"
                >
                  {moneyText(row.value, currency)}
                </text>
              </g>
            );
          })}
        </svg>
      </ChartFrame>
      {hasParts && partLabel ? (
        <Legend
          items={[
            { key: 'due', label: 'Not yet due', colour: 'slate', kind: 'tint' },
            { key: 'part', label: partLabel, colour: 'slate', kind: 'rect' },
          ]}
        />
      ) : null}
    </div>
  );
}
